import { useTinyHost } from "@tiny/host-contract";
import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@tiny/components";
import {
  useGetGlobalActiveEngagerSettingsQuery,
  usePutGlobalActiveEngagerSettingsMutation,
} from "@tiny/api";
import { useQueryClient } from "react-query";

interface GlobalActiveEngagerSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

function GlobalActiveEngagerSettingsDialog({
  open,
  onClose,
}: GlobalActiveEngagerSettingsDialogProps) {
  const { capabilities } = useTinyHost();
  const accountId = capabilities.accountId ?? "";
  const queryClient = useQueryClient();

  const { data, isLoading } = useGetGlobalActiveEngagerSettingsQuery(
    accountId,
    {
      enabled: open && !!accountId,
    }
  );

  const [createdInDays, setCreatedInDays] = React.useState<string>("");
  const [openedInDays, setOpenedInDays] = React.useState<string>("");
  const [clickedInDays, setClickedInDays] = React.useState<string>("");
  const [source, setSource] = React.useState<string>("");

  React.useEffect(() => {
    if (data) {
      setCreatedInDays(
        data.createdInDays != null ? String(data.createdInDays) : ""
      );
      setOpenedInDays(
        data.openedInDays != null ? String(data.openedInDays) : ""
      );
      setClickedInDays(
        data.clickedInDays != null ? String(data.clickedInDays) : ""
      );
      setSource(data.source != null ? data.source : "");
    }
  }, [data]);

  const { mutate: saveSettings, isLoading: isSaving } =
    usePutGlobalActiveEngagerSettingsMutation(accountId, {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "GET",
          `/global-active-engager/${accountId}/settings`,
        ]);
        onClose();
      },
    });

  const handleSave = () => {
    saveSettings({
      createdInDays: createdInDays !== "" ? Number(createdInDays) : undefined,
      openedInDays: openedInDays !== "" ? Number(openedInDays) : undefined,
      clickedInDays: clickedInDays !== "" ? Number(clickedInDays) : undefined,
      source: source ?? undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configure Global Active Segment</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Typography variant="body2" color="grey" tone={600}>
            Loading settings...
          </Typography>
        ) : (
          <Grid container spacing={3} style={{ paddingTop: 8 }}>
            <Grid item xs={12}>
              <TextField
                label="Audiences Created In (days)"
                value={createdInDays}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCreatedInDays(e.target.value)
                }
                type="number"
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Opened In (days)"
                value={openedInDays}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOpenedInDays(e.target.value)
                }
                type="number"
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Clicked In (days)"
                value={clickedInDays}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setClickedInDays(e.target.value)
                }
                type="number"
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Source"
                value={source}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSource(e.target.value)
                }
                type="text"
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={isLoading || isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GlobalActiveEngagerSettingsDialog;
